import React from 'react';
import {
  Box, Flex, Heading, Text, VStack, Button, Input, InputGroup, InputRightElement,
  FormControl, FormLabel, useToast, Link,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaSnowflake } from 'react-icons/fa';
import { login } from '../Utilities/networks';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const toast = useToast();

  React.useEffect(() => {
    if (sessionStorage.getItem('token')) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await login({ email, password });
      if (response.status === 200) {
        sessionStorage.setItem('token', response.data.message);
        if (response.data.data.permissions) {
          sessionStorage.setItem('permissions', JSON.stringify(response.data.data.permissions));
        } else {
          sessionStorage.removeItem('permissions');
        }
        sessionStorage.setItem('tenant_id', response.data.data.tenant_id);
        sessionStorage.setItem('is_admin', response.data.data.is_admin ? 'true' : 'false');
        navigate('/', { replace: true });
        window.location.reload();
      } else {
        toast({ title: 'Login failed', description: response.data.message || 'Login failed', status: 'error', duration: 5000, isClosable: true });
      }
    } catch (err) {
      let errorMessage = 'An unexpected error occurred';
      if (err.response) {
        errorMessage = err.response.data?.message || 'Login failed';
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection.';
      }
      toast({ title: 'Login failed', description: errorMessage, status: 'error', duration: 5000, isClosable: true });
    }
  };

  return (
    <Flex minH="100vh" bg="slate.900" position="relative" overflow="hidden">
      <Box
        position="absolute"
        inset={0}
        bgGradient="radial(circle at 20% 20%, brand.800 0%, transparent 50%), radial(circle at 80% 80%, brand.900 0%, transparent 40%)"
        opacity={0.6}
      />
      <Box
        position="absolute"
        top="-20%"
        right="-10%"
        w="500px"
        h="500px"
        borderRadius="full"
        bg="brand.500"
        filter="blur(120px)"
        opacity={0.15}
      />

      <Flex
        w="full"
        maxW="1100px"
        mx="auto"
        my="auto"
        p={{ base: 4, md: 8 }}
        align="center"
        justify="center"
        position="relative"
        zIndex={1}
      >
        <Flex
          w="full"
          direction={{ base: 'column', md: 'row' }}
          bg="white"
          borderRadius="2xl"
          overflow="hidden"
          boxShadow="card"
          minH={{ md: '520px' }}
        >
          <Flex
            flex={1}
            display={{ base: 'none', md: 'flex' }}
            direction="column"
            justify="center"
            p={10}
            bg="slate.900"
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              inset={0}
              bgGradient="linear(135deg, brand.700 0%, slate.900 60%)"
              opacity={0.9}
            />
            <VStack align="flex-start" spacing={6} position="relative" zIndex={1}>
              <Flex w={12} h={12} borderRadius="xl" bg="brand.500" align="center" justify="center">
                <FaSnowflake color="white" size={22} />
              </Flex>
              <Heading color="white" fontFamily="heading" fontSize="3xl" fontWeight="700" lineHeight="1.2">
                TerraCool<br />Operations
              </Heading>
              <Text color="slate.300" fontSize="md" maxW="280px" lineHeight="1.7">
                Manage complaints, technicians, and field services from one modern control center.
              </Text>
            </VStack>
          </Flex>

          <Box flex={1} p={{ base: 8, md: 10 }}>
            <VStack align="stretch" spacing={8}>
              <Box>
                <Heading size="lg" fontFamily="heading" color="slate.800" mb={1}>
                  Sign in
                </Heading>
                <Text color="slate.500" fontSize="sm">Enter your credentials to continue</Text>
              </Box>

              <form onSubmit={handleSubmit}>
                <VStack spacing={5}>
                  <FormControl isRequired>
                    <FormLabel color="slate.600" fontSize="sm" fontWeight="600">Email</FormLabel>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      size="lg"
                      borderRadius="lg"
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel color="slate.600" fontSize="sm" fontWeight="600">Password</FormLabel>
                    <InputGroup size="lg">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password"
                        borderRadius="lg"
                      />
                      <InputRightElement>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>

                  <Flex w="full" justify="space-between" fontSize="sm">
                    <Text color="slate.500">
                      No account?{' '}
                      <Link color="brand.600" fontWeight="600" onClick={() => navigate('/register')}>
                        Register
                      </Link>
                    </Text>
                    <Link color="brand.600" fontWeight="600">Forgot password?</Link>
                  </Flex>

                  <Button type="submit" w="full" size="lg" colorScheme="brand" borderRadius="lg" mt={2}>
                    Sign In
                  </Button>
                </VStack>
              </form>
            </VStack>
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default Login;
